import React, { useCallback, useMemo } from "react";
import { Task } from "@common/types/task";
import {
    AccountingReportType,
    ACCOUNTING_REPORT_COMPARISON_OPTIONS,
} from "@common/types/reports/report-type";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import {
    toDateOnlyDateRange,
    toLocalDateRange,
} from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import { AccountingReportCycleSelect } from "@components/accounting/AccountingReportCycleSelect";
import { AccountingReportComparisonSelect } from "@components/accounting/AccountingReportComparisonSelect";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { SpreadsheetGrid } from "@components/spreadsheet/SpreadsheetGrid";
import { ColumnDef } from "@components/spreadsheet/types";
import { pluralizeWithCount } from "@common/helpers/string";
import { usePendingReferenceContextMaybe } from "@components/tasks/SpreadsheetRefContext";
import { useCellRangeChatAction } from "@components/tasks/chatDrag/useCellRangeChatAction";
import { useSpreadsheetSelection } from "@components/spreadsheet/useSpreadsheetSelection";
import { GlDrilldownDrawer } from "@components/accounting/generalLedger/GlDrilldownDrawer";
import { useGlDrilldownDrawerState } from "@components/accounting/useGlDrilldownDrawerState";
import { getDrillDownClassParams } from "@components/accounting/useGLDrillDownUrl";
import { buildGlDrillDownParams } from "@components/accounting/glDrillDown.lib";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { useTaskReportFilters } from "../shared/useTaskReportFilters";
import { useViewConfigSync } from "../shared/useViewConfigSync";
import { ExpandCollapseAllButton } from "../shared/ExpandCollapseAllButton";
import { useExpandCollapseAll } from "../shared/useExpandCollapseAll";
import { hasSingleValueColumn } from "@components/accounting/singleColumnReport";
import {
    REPORT_LOOK_LABEL_COLUMN,
    toReportLookColumns,
} from "../shared/reportLookColumns";
import {
    buildPeriodColumns,
    PeriodDrillDownHandler,
} from "./balanceSheetTaskColumns";
import { getDefaultExpandedIds } from "./balanceSheetTaskRows";
import { BalanceSheetTaskCommandBar } from "./BalanceSheetTaskCommandBar";
import { useBalanceSheetReport } from "./useBalanceSheetReport";
import styles from "./BalanceSheetTaskBody.module.scss";

interface Props {
    selectedTask: Task;
}

export const BalanceSheetTaskBody: React.FC<Props> = ({ selectedTask }) => {
    const isBlocked = (selectedTask.numberOfActiveBlockedByTasks ?? 0) > 0;
    const pendingRefCtx = usePendingReferenceContextMaybe();

    const {
        localDateRange,
        setLocalDateRange,
        localCycle,
        setLocalCycle,
        comparisonOptions,
        setComparisonOptions,
    } = useTaskReportFilters(selectedTask);

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_BALANCE_SHEET_TASK_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    const entityId = selectedTask.entityIds?.[0];
    const { balanceSheet, rows, ledgerId } = useBalanceSheetReport({
        entityId,
        dateRange: localDateRange,
        cycle: localCycle,
        comparisonOptions,
    });

    const {
        openDrillDown,
        closeDrillDown,
        isOpen: isGlDrawerOpen,
        drawerKey,
        drawerTarget,
    } = useGlDrilldownDrawerState();

    const reportEntityIds = useMemo(
        () => [
            ...new Set(
                (balanceSheet.data?.periods ?? []).flatMap((period) =>
                    period.entityId !== undefined ? [period.entityId] : [],
                ),
            ),
        ],
        [balanceSheet.data],
    );

    const handlePeriodDrillDown = useCallback<PeriodDrillDownHandler>(
        (row, idx) => {
            const period = row._periods?.[idx];
            if (!period) {
                return;
            }
            const isEntitiesTotalColumn =
                period.entityId === undefined && reportEntityIds.length > 0;
            openDrillDown(
                buildGlDrillDownParams({
                    startDate: period.startDate,
                    endDate: period.endDate,
                    ...(isEntitiesTotalColumn
                        ? { entityIds: reportEntityIds }
                        : { entityId: period.entityId ?? entityId }),
                    ledgerId,
                    accountCodes: row._accountCodes ?? [],
                    ...getDrillDownClassParams(period, { cycle: localCycle }),
                }),
            );
        },
        [openDrillDown, reportEntityIds, entityId, ledgerId, localCycle],
    );

    const columns = useMemo((): ColumnDef[] => {
        if (!balanceSheet.data) {
            return [REPORT_LOOK_LABEL_COLUMN];
        }
        const { periods } = balanceSheet.data;
        return toReportLookColumns({
            periods,
            labelColumn: REPORT_LOOK_LABEL_COLUMN,
            periodColumns: buildPeriodColumns({
                periods,
                onDrillDown: handlePeriodDrillDown,
            }),
        });
    }, [balanceSheet.data, handlePeriodDrillDown]);

    const defaultExpandedIds = useMemo(
        () => (rows ? getDefaultExpandedIds(rows) : []),
        [rows],
    );

    const {
        expandedIds,
        setExpandedIds,
        expandedIdsSeed,
        isCollapsed,
        handleExpandAll,
        handleCollapseAll,
    } = useExpandCollapseAll(rows, defaultExpandedIds);

    const { viewConfig, refCtx } = useViewConfigSync({
        localDateRange,
        localCycle,
        comparisonOptions,
        setLocalDateRange,
        setLocalCycle,
        setComparisonOptions,
        rows,
    });

    const { columnsWithHighlight, handleSelectionRangeChange } =
        useSpreadsheetSelection({
            taskId: selectedTask.id,
            rows,
            columns,
            expandedIds,
            source: "balance_sheet",
            refCtx,
            highlightedCellClassName: styles.highlightedCell,
            viewConfig,
            onPendingReferenceChange: pendingRefCtx?.setPendingReference,
        });
    const rangeAction = useCellRangeChatAction();
    const isGridShown = balanceSheet.isSuccess && !!balanceSheet.data && !!rows;

    return (
        <div className={styles.container}>
            {isBlocked && (
                <div className={styles.blockedMessage}>
                    {pluralizeWithCount(
                        "task",
                        selectedTask.numberOfActiveBlockedByTasks ?? 0,
                    )}{" "}
                    {(selectedTask?.numberOfActiveBlockedByTasks ?? 0) > 1
                        ? "are"
                        : "is"}{" "}
                    blocking your Balance Sheet
                </div>
            )}
            <div className={styles.toolbar}>
                <div className={styles.filterGroup}>
                    <DateRangeFilterNext
                        dateRange={toDateOnlyDateRange(localDateRange)}
                        onChange={(range) =>
                            setLocalDateRange(toLocalDateRange(range))
                        }
                        buttonClass={styles.dateRangeFilter}
                        pinnedPresets={pinnedPresets}
                        onTogglePin={onTogglePin}
                    />
                    <AccountingReportCycleSelect
                        value={localCycle}
                        onChange={setLocalCycle}
                        showEntityOption
                        showClassOption
                        className={styles.cycleSelect}
                    />
                </div>
                <AccountingReportComparisonSelect
                    comparisons={comparisonOptions.comparisons}
                    comparisonDiffs={comparisonOptions.comparisonDiffs}
                    ratioTypes={comparisonOptions.ratioTypes}
                    availableOptions={
                        ACCOUNTING_REPORT_COMPARISON_OPTIONS[
                            AccountingReportType.BALANCE_SHEET
                        ]
                    }
                    onChange={setComparisonOptions}
                />
                <div className="ml-auto">
                    <ExpandCollapseAllButton
                        isCollapsed={isCollapsed}
                        onExpandAll={handleExpandAll}
                        onCollapseAll={handleCollapseAll}
                    />
                </div>
            </div>
            <div className={styles.tableSection}>
                <LoaderOnInitialLoading queryResult={balanceSheet}>
                    {balanceSheet.data && rows && (
                        <SpreadsheetGrid
                            data={rows}
                            columns={columnsWithHighlight}
                            appearance="report"
                            centerColumns={hasSingleValueColumn(
                                balanceSheet.data.periods,
                            )}
                            expandable
                            defaultExpandedIds={expandedIdsSeed}
                            clearSelectionOnBlur={false}
                            nestingIndentPx={24}
                            overflow="auto"
                            stickyHeader
                            readonly
                            disableDragFill
                            noOuterBorder
                            onExpandedChange={setExpandedIds}
                            onSelectionRangeChange={handleSelectionRangeChange}
                            rangeAction={rangeAction}
                            floatingFooter={
                                <CommandIsland sticky alignEnd>
                                    <BalanceSheetTaskCommandBar />
                                </CommandIsland>
                            }
                        />
                    )}
                </LoaderOnInitialLoading>
            </div>
            {!balanceSheet.isLoading && !isGridShown && (
                <CommandIsland sticky alignEnd className={styles.toolbar}>
                    <BalanceSheetTaskCommandBar />
                </CommandIsland>
            )}
            {drawerTarget && (
                <GlDrilldownDrawer
                    key={drawerKey}
                    open={isGlDrawerOpen}
                    onClose={closeDrillDown}
                    filters={drawerTarget.filters}
                    entityIds={drawerTarget.entityIds}
                    ledgerBasis={AccountingLedgerBasis.cash}
                    classifications={drawerTarget.classifications}
                    pnlBalanceSince={drawerTarget.pnlBalanceSince}
                />
            )}
        </div>
    );
};
